/* size-fit-guide.js — <size-fit-guide> and <size-guide-video>
   (sections/size-fit-guide.liquid, blocks/_size-guide-*.liquid).

   <size-fit-guide> is the PDP's "Size, Fit + Switch Guide" drawer. It opens
   from any [data-size-guide-open] control on the page — the size picker's
   guide link renders one — and behaves like the theme's other drawers:

   * opening resets to the first tab and to the top of the panel; closing keeps
     the tab being read, so the slide-out still shows it;
   * the page behind is scroll-locked through window.themeScrollLock (a
     relative import of global.js would run a second copy of its lock state)
     and Tab / Shift+Tab cycle inside the panel while it is open;
   * the close button, a click on the scrim, or Escape closes it and hands
     focus back to the control that opened it.

   The motion is CSS: [data-open] flips the transitions and every layer keeps
   its own delayed `visibility` transition, so closing needs no timer.

   <size-guide-video> is the Fit Guide's tutorial player on a plain <video>;
   see blocks/_size-guide-video.liquid for the markup contract.

   Without this file the guide link stays a link to the guide page (or hidden
   when it has none) and the drawer never opens. */

const TRIGGER = '[data-size-guide-open]';
const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'iframe',
  'video[controls]',
  '[tabindex]:not([tabindex="-1"])'
].join(',');

/* Pointer-idle delay before the video controls fade out. */
const CONTROLS_IDLE_MS = 2500;

/* Sections the theme editor currently has selected. A setting change
   re-renders the section, which replaces this element; the new one reopens
   straight away so the merchant keeps looking at what they are editing. */
const editorSelected = new Set();

const designMode = () => Boolean(window.Shopify && window.Shopify.designMode);

class SizeFitGuide extends HTMLElement {
  connectedCallback() {
    this.panel = this.querySelector('.fs-size-guide__panel');
    if (!this.panel) return;
    this.sectionId = this.dataset.sectionId;
    this.tabs = this.buildTabs();
    this.isOpen = false;
    this.lastFullscreenChange = 0;

    /* Added on open and removed on close, so they need stable references. */
    this.onKeyup = this.onKeyup.bind(this);
    this.onTrapKeydown = this.onTrapKeydown.bind(this);

    /* One signal for every listener, so a reconnect (the editor moving the
       section) never stacks a second copy. */
    this.listeners = new AbortController();
    const { signal } = this.listeners;
    document.addEventListener('click', (event) => this.onDocumentClick(event), { signal });
    document.addEventListener('keydown', (event) => this.onDocumentKeydown(event), { signal });
    document.addEventListener('fullscreenchange', () => { this.lastFullscreenChange = Date.now(); }, { signal });
    this.addEventListener('click', (event) => this.onClick(event), { signal });
    this.addEventListener('keydown', (event) => this.onTabKeydown(event), { signal });

    /* The trigger lives in main-product; the editor can re-render that section
       on its own, and the fresh copy has to be claimed again. */
    document.addEventListener('shopify:section:load', () => this.claimTriggers(), { signal });
    ['shopify:section:select', 'shopify:section:deselect', 'shopify:block:select'].forEach((type) => {
      document.addEventListener(type, (event) => this.onEditorEvent(event), { signal });
    });

    this.claimTriggers();

    if (designMode() && editorSelected.has(this.sectionId)) this.open(null, true);
  }

  disconnectedCallback() {
    if (!this.panel) return;
    this.listeners.abort();
    if (this.isOpen) this.close();
    this.releaseTriggers();
  }

  /* --- triggers ----------------------------------------------------------

     A trigger is rendered in one of two shapes, and both become a button for
     a dialog once a guide is on the page:
     * a <button> that waits, invisible, for a guide to claim it
       ([data-size-guide-pending]) — it has nowhere else to go;
     * an <a> to the merchant's guide page, which keeps working as a link when
       this section is not on the template. Claimed, it announces as a button,
       and a modified click (new tab, new window) still follows the link. */

  claimTriggers() {
    document.querySelectorAll(TRIGGER).forEach((trigger) => {
      trigger.removeAttribute('data-size-guide-pending');
      trigger.setAttribute('aria-haspopup', 'dialog');
      trigger.setAttribute('aria-controls', this.panel.id);
      trigger.setAttribute('aria-expanded', String(this.isOpen));
      if (trigger.tagName === 'A') trigger.setAttribute('role', 'button');
    });
  }

  releaseTriggers() {
    document.querySelectorAll(TRIGGER).forEach((trigger) => {
      if (trigger.tagName === 'A') {
        ['role', 'aria-haspopup', 'aria-controls', 'aria-expanded'].forEach((name) => trigger.removeAttribute(name));
      } else {
        trigger.setAttribute('data-size-guide-pending', '');
      }
    });
  }

  setExpanded(expanded) {
    document.querySelectorAll(TRIGGER).forEach((trigger) => {
      trigger.setAttribute('aria-expanded', String(expanded));
    });
  }

  onDocumentClick(event) {
    const trigger = event.target.closest?.(TRIGGER);
    if (!trigger) return;
    if (trigger.tagName === 'A' && (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)) return;
    event.preventDefault();
    this.open(trigger);
  }

  /* An <a> announced as a button must answer Space as a button does. */
  onDocumentKeydown(event) {
    if (event.key !== ' ') return;
    const target = event.target;
    if (!target || target.tagName !== 'A' || !target.matches(TRIGGER)) return;
    event.preventDefault();
    this.open(target);
  }

  /* --- open / close ------------------------------------------------------- */

  open(opener, instant) {
    if (this.isOpen) return;
    this.isOpen = true;
    this.opener = opener && opener.isConnected ? opener : null;

    /* The tab resets on open, not on close, so the closing slide keeps the tab
       that was being read. */
    this.selectTab(this.tabs[0]);

    this.panel.removeAttribute('inert');
    this.panel.scrollTop = 0;

    if (instant) {
      this.setAttribute('data-instant', '');
      requestAnimationFrame(() => requestAnimationFrame(() => this.removeAttribute('data-instant')));
    }
    this.setAttribute('data-open', '');
    window.themeScrollLock?.lock(this);

    this.setExpanded(true);
    document.addEventListener('keyup', this.onKeyup);
    document.addEventListener('keydown', this.onTrapKeydown);
    /* [data-open] flips `visibility` at the start of its transition, so the
       panel is already focusable here. */
    this.panel.focus({ preventScroll: true });
  }

  close() {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.removeAttribute('data-open');
    this.panel.setAttribute('inert', '');
    /* Every tutorial stops and drops its <video> here, including one still
       loading: a closed drawer holds no decoder and plays no sound. */
    this.querySelectorAll('size-guide-video').forEach((player) => player.reset?.());

    document.removeEventListener('keyup', this.onKeyup);
    document.removeEventListener('keydown', this.onTrapKeydown);

    window.themeScrollLock?.unlock(this);
    this.setExpanded(false);
    this.restoreFocus();
  }

  /* Back to the control that opened the guide. It can be gone by now — the
     theme editor re-renders sections — so any trigger still on the page is the
     next best place, then <main>, rather than dropping the shopper on <body>
     at the top of the document. */
  restoreFocus() {
    const candidates = [this.opener, ...document.querySelectorAll(TRIGGER), document.getElementById('MainContent')];
    for (const candidate of candidates) {
      if (!candidate || !candidate.isConnected || typeof candidate.focus !== 'function') continue;
      candidate.focus({ preventScroll: true });
      if (document.activeElement === candidate) return;
    }
  }

  /* Escape closes on keyup — except for the Escape that takes a tutorial out
     of fullscreen, which the browser consumes as its keydown. Its keyup can
     land either side of the fullscreenchange, so both are checked. */
  onKeyup(event) {
    if (event.key !== 'Escape') return;
    if (document.fullscreenElement || Date.now() - this.lastFullscreenChange < 1000) return;
    this.close();
  }

  /* Only real tab stops count as the ends of the loop. Unselected tabs are
     tabindex="-1" and a resting video controller is `visibility: hidden`; both
     still have boxes, and taking one for the last stop would let Tab walk off
     the final real control into the page behind. */
  onTrapKeydown(event) {
    if (event.key !== 'Tab') return;
    /* A tutorial in fullscreen covers the panel without making it inert, so
       while it is fullscreen the loop narrows to the player. */
    const fullscreen = document.fullscreenElement;
    const scope = fullscreen && this.panel.contains(fullscreen) ? fullscreen : this.panel;
    const items = Array.from(scope.querySelectorAll(FOCUSABLE)).filter((el) => {
      if (el.getAttribute('tabindex') === '-1' || !el.getClientRects().length) return false;
      return getComputedStyle(el).visibility !== 'hidden';
    });
    const active = document.activeElement;

    if (!items.length) {
      event.preventDefault();
      this.panel.focus({ preventScroll: true });
      return;
    }

    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey) {
      if (active === first || active === scope || !scope.contains(active)) {
        event.preventDefault();
        last.focus();
      }
    } else if (active === last || !scope.contains(active)) {
      event.preventDefault();
      first.focus();
    }
  }

  onClick(event) {
    if (event.target.closest('[data-size-guide-close]')) {
      this.close();
      return;
    }
    const tab = event.target.closest('[role="tab"]');
    if (tab && this.tabs.includes(tab)) this.selectTab(tab);
  }

  /* --- tabs ----------------------------------------------------------------

     One button per panel, built from the label each tab block writes on its
     panel: the section's Liquid gets its theme blocks' ids but not their
     settings, so it cannot print the labels itself.

     Automatic activation: arrows move and select at once, as a click does.
     Switching is an instant swap, and a tutorial whose tab is left starts
     over when it is shown again. */

  buildTabs() {
    const list = this.querySelector('[data-size-guide-tabs]');
    if (!list) return [];
    const panels = this.querySelectorAll('.fs-size-guide__panels > .fs-size-guide__tab-panel');

    list.textContent = '';
    return Array.from(panels, (panel, index) => {
      const tab = document.createElement('button');
      tab.type = 'button';
      tab.className = 'fs-size-guide__tab';
      tab.id = panel.getAttribute('aria-labelledby') || `${panel.id}-tab`;
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-controls', panel.id);
      tab.setAttribute('aria-selected', String(index === 0));
      tab.tabIndex = index === 0 ? 0 : -1;
      tab.textContent = panel.dataset.tabLabel || '';
      list.appendChild(tab);
      return tab;
    });
  }

  selectTab(tab) {
    if (!tab) return;
    this.tabs.forEach((item) => {
      const selected = item === tab;
      item.setAttribute('aria-selected', String(selected));
      item.tabIndex = selected ? 0 : -1;

      const panel = document.getElementById(item.getAttribute('aria-controls'));
      if (!panel) return;
      if (selected) {
        panel.hidden = false;
      } else if (!panel.hidden) {
        panel.hidden = true;
        panel.querySelectorAll('size-guide-video').forEach((player) => player.reset?.());
      }
    });
  }

  onTabKeydown(event) {
    const tab = event.target.closest?.('[role="tab"]');
    const index = tab ? this.tabs.indexOf(tab) : -1;
    if (index === -1) return;

    const count = this.tabs.length;
    const forward = document.documentElement.dir === 'rtl' ? 'ArrowLeft' : 'ArrowRight';
    const back = forward === 'ArrowRight' ? 'ArrowLeft' : 'ArrowRight';
    let next = null;

    if (event.key === forward) next = this.tabs[(index + 1) % count];
    else if (event.key === back) next = this.tabs[(index - 1 + count) % count];
    else if (event.key === 'Home') next = this.tabs[0];
    else if (event.key === 'End') next = this.tabs[count - 1];
    if (!next) return;

    event.preventDefault();
    this.selectTab(next);
    next.focus();
  }

  /* --- theme editor ------------------------------------------------------- */

  onEditorEvent(event) {
    if (event.detail?.sectionId !== this.sectionId) return;

    if (event.type === 'shopify:section:deselect') {
      editorSelected.delete(this.sectionId);
      this.close();
      return;
    }

    editorSelected.add(this.sectionId);
    this.open();

    if (event.type !== 'shopify:block:select') return;
    const panel = event.target.closest?.('.fs-size-guide__tab-panel');
    if (!panel) return;
    const tab = this.tabs.find((item) => item.getAttribute('aria-controls') === panel.id);
    if (tab) this.selectTab(tab);
    if (event.target !== panel) event.target.scrollIntoView?.({ block: 'nearest' });
  }
}

if (!customElements.get('size-fit-guide')) customElements.define('size-fit-guide', SizeFitGuide);

/* --------------------------------------------------------------------------
   <size-guide-video>

   * The <video> only exists while `playing || pausedByUser`. Ending sets
     `playing` false without touching `pausedByUser`, which removes it;
     scrolling out of view mid-play pauses it; closing the drawer or leaving
     the tab resets the player outright.
   * `ready` survives that removal, so a replay hides the poster at once and
     the new element shows without a second fade-in.
   * The poster hides only when `ready && posterHidden`, so it stays up until
     the first `canplay`.
   * State is read back from the element as well as written to it: a pause,
     play or mute made outside these controls (iOS's native fullscreen player,
     hardware media keys) arrives through its events, as in
     social-video-carousel.js.
   -------------------------------------------------------------------------- */

const initialState = () => ({
  showCaption: true,
  playing: false,
  ready: false,
  pausedByUser: false,
  posterHidden: false,
  controlsActive: false,
  muted: false,
  played: 0,
  loaded: 0,
  fullscreen: false
});

/* 75 -> "1:15" */
const formatTime = (seconds) => {
  const whole = Math.max(0, Math.floor(seconds || 0));
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`;
};

/* A control inside a layer that is fading in only computes visible once the
   layer's `visibility` transition has started, a frame or two after the class
   change; until then it refuses focus. By default the focus scrolls: the new
   control can sit below the panel's edge or under its sticky header (which
   the panel's scroll padding clears). */
const focusWhenVisible = (element, options) => {
  let frames = 0;
  const attempt = () => {
    if (!element.isConnected) return;
    if (getComputedStyle(element).visibility === 'visible') element.focus(options);
    else if (frames++ < 30) requestAnimationFrame(attempt);
  };
  attempt();
};

class SizeGuideVideo extends HTMLElement {
  connectedCallback() {
    this.src = this.dataset.src || '';
    this.caption = this.querySelector('[data-video-caption]');
    this.startButton = this.querySelector('[data-video-start]');
    this.player = this.querySelector('[data-video-player]');
    this.mediaHost = this.querySelector('[data-video-media]');
    this.controller = this.querySelector('[data-video-controller]');
    this.poster = this.querySelector('[data-video-poster]');
    this.progress = this.querySelector('[data-video-progress]');
    this.seek = this.querySelector('[data-video-seek]');
    this.toggleButton = this.querySelector('[data-video-toggle]');
    this.muteButton = this.querySelector('[data-video-mute]');
    this.fullscreenButton = this.querySelector('[data-video-fullscreen]');

    this.state = initialState();
    this.video = null;
    this.seeking = false;
    this.appliedPlaying = null;
    /* Kept by the IntersectionObserver below; assumed true without one. */
    this.inView = true;

    this.listeners = new AbortController();
    const { signal } = this.listeners;
    const wake = () => this.wakeControls();
    this.addEventListener('click', (event) => this.onClick(event), { signal });
    /* pointerup as well as pointermove: a still tap on a touch screen moves
       nothing, and must still bring the controls up. */
    this.player?.addEventListener('pointermove', wake, { signal });
    this.player?.addEventListener('pointerup', wake, { signal });
    /* A focused timeline is not ticked along during playback (see render), so
       it catches up before the browser steps it for a key press. */
    this.seek?.addEventListener('keydown', () => this.syncSeek(), { signal });
    this.seek?.addEventListener('input', () => this.onSeekInput(), { signal });
    this.seek?.addEventListener('change', () => this.onSeekChange(), { signal });
    ['fullscreenchange', 'webkitfullscreenchange'].forEach((type) => {
      document.addEventListener(type, () => this.onFullscreenChange(), { signal });
    });

    this.fadeInPosters();

    if (this.player && 'IntersectionObserver' in window) {
      this.observer = new IntersectionObserver((entries) => {
        const entry = entries[entries.length - 1];
        this.inView = entry.isIntersecting;
        if (entry.isIntersecting || !this.state.playing) return;
        /* A clip under way pauses as if the shopper had paused it: the <video>
           stays, with its frame and position, so the controls can seek and
           resume it. One that has not got going yet (still loading, or no
           progress to show) goes back to the caption instead: stopping it
           half-way would leave neither the start button nor the controls,
           and left alone it would start unseen. */
        if (this.state.ready && this.state.played > 0) this.setState({ playing: false, pausedByUser: true });
        else this.returnToCaption(() => this.reset());
      });
      this.observer.observe(this.player);
    }

    this.render();
  }

  disconnectedCallback() {
    this.listeners.abort();
    this.observer?.disconnect();
    clearTimeout(this.idleTimer);
    this.removeVideo();
  }

  /* Back to the untouched player: caption and poster up, no <video>. */
  reset() {
    if (!this.state) return;
    clearTimeout(this.idleTimer);
    this.seeking = false;
    this.focusControlsWhenReady = false;
    this.duration = 0;
    this.state = initialState();
    this.render();
  }

  setState(patch) {
    Object.assign(this.state, patch);
    this.render();
  }

  /* A cached poster can finish before this runs, so `complete` is checked as
     well as `load` listened for. */
  fadeInPosters() {
    this.querySelectorAll('[data-video-fade]').forEach((fade) => {
      const img = fade.querySelector('img');
      if (!img) return;
      const show = () => fade.classList.add('is-loaded');
      if (img.complete && img.naturalWidth > 0) show();
      else img.addEventListener('load', show, { once: true });
    });
  }

  /* --- the <video> -------------------------------------------------------- */

  createVideo() {
    const video = document.createElement('video');
    video.className = 'fs-size-guide-video__video';
    video.playsInline = true;
    video.autoplay = true;
    video.preload = 'auto';
    video.muted = this.state.muted;
    video.src = this.src;

    this.videoListeners = new AbortController();
    const { signal } = this.videoListeners;
    const progress = () => this.onProgress();
    video.addEventListener('canplay', () => this.onCanPlay(), { signal });
    video.addEventListener('timeupdate', progress, { signal });
    video.addEventListener('progress', progress, { signal });
    video.addEventListener('ended', () => this.onEnded(), { signal });
    video.addEventListener('play', () => this.onMediaPlay(), { signal });
    video.addEventListener('pause', () => this.onMediaPause(), { signal });
    video.addEventListener('volumechange', () => {
      if (video.muted !== this.state.muted) this.setState({ muted: video.muted });
    }, { signal });
    /* A source that cannot load (a mistyped file name, a 404) puts the
       caption and its start button back rather than leaving a dead poster. */
    video.addEventListener('error', () => this.returnToCaption(() => this.reset()), { signal });

    this.mediaHost.appendChild(video);
    this.video = video;
    this.appliedPlaying = null;
  }

  removeVideo() {
    const video = this.video;
    if (!video) return;
    this.video = null;
    this.videoListeners.abort();
    video.pause();
    /* Dropping the source is what actually releases the decoder and stops the
       network fetch; removing the element alone does neither at once. */
    video.removeAttribute('src');
    video.load();
    video.remove();
    if (document.fullscreenElement === this.player) document.exitFullscreen?.().catch(() => {});
  }

  onCanPlay() {
    this.duration = this.video?.duration || this.duration;
    if (!this.focusControlsWhenReady) {
      if (!this.state.ready) this.setState({ ready: true });
      return;
    }
    /* A keyboard start kept the caption, and focus on its button, through the
       load. Both hand over now — focus only if the shopper has not moved on. */
    this.focusControlsWhenReady = false;
    const focusOnStart = document.activeElement === this.startButton;
    this.setState({ ready: true, showCaption: false });
    this.wakeControls();
    if (focusOnStart && this.toggleButton) focusWhenVisible(this.toggleButton);
  }

  onProgress() {
    if (this.seeking || !this.video) return;
    const { currentTime, duration, buffered } = this.video;
    if (!duration) return;
    const patch = { played: currentTime / duration };
    if (buffered.length > 0) patch.loaded = buffered.end(buffered.length - 1) / duration;
    this.setState(patch);
  }

  onEnded() {
    this.returnToCaption(() => this.setState({ posterHidden: false, playing: false, pausedByUser: false, showCaption: true, played: 0 }));
  }

  /* Runs a change that brings the caption and poster back over the controls.
     Focus left in the controls would sit on buttons the poster now covers, so
     the start button, back with the caption, takes it instead. */
  returnToCaption(change) {
    const focusWasInControls = Boolean(this.controller?.contains(document.activeElement));
    change();
    if (!focusWasInControls || !this.startButton) return;
    /* Scrolled to only while the player is in view: a player the shopper has
       scrolled away from stays where they left it. */
    focusWhenVisible(this.startButton, { preventScroll: !this.inView });
  }

  /* A play or pause these controls did not ask for. The `pause` that precedes
     `ended` is left to onEnded. */
  onMediaPlay() {
    if (this.state.playing) return;
    this.appliedPlaying = true;
    this.setState({ playing: true, pausedByUser: false, posterHidden: true, showCaption: false });
  }

  onMediaPause() {
    if (!this.state.playing || this.video?.ended) return;
    this.appliedPlaying = false;
    this.setState({ playing: false, pausedByUser: true });
  }

  /* --- controls ----------------------------------------------------------- */

  /* Every start dismisses the caption, whichever control it came from — a
     replay from the controls after the clip ended included — unless the
     keyboard start above asks to keep it through the load. */
  startPlaying(patch = {}) {
    this.setState({ showCaption: false, ...patch, playing: true, posterHidden: true });
  }

  togglePlaying() {
    if (!this.src) return;
    if (this.state.playing) this.setState({ pausedByUser: true, playing: false });
    else this.startPlaying({ pausedByUser: false });
  }

  onClick(event) {
    const target = event.target;
    if (target.closest('[data-video-start]')) {
      if (this.src) {
        /* A pointer start dismisses the caption at once. A keyboard press (no
           pointer, so detail 0) keeps it — and focus on this button — until
           the clip can play, then hands focus to the controls; hiding the
           focused button now would drop focus on <body> for the whole load. */
        /* Once a start is under way (a keyboard start keeps this button up
           through the load) a second press must not re-arm the hand-off. */
        if (this.state.playing) return;
        const keyboard = event.detail === 0;
        this.focusControlsWhenReady = keyboard;
        this.startPlaying({ showCaption: keyboard, pausedByUser: false });
      }
      return;
    }
    if (target.closest('[data-video-toggle]')) {
      this.togglePlaying();
      return;
    }
    if (target.closest('[data-video-mute]')) {
      this.setState({ muted: !this.state.muted });
      return;
    }
    if (target.closest('[data-video-fullscreen]')) {
      this.toggleFullscreen();
      return;
    }
    if (this.controller?.contains(target)) return;
    if (this.player?.contains(target)) this.togglePlaying();
  }

  /* Shows the controls and fades them again after CONTROLS_IDLE_MS without
     another pointer movement or tap. */
  wakeControls() {
    if (!this.state.ready) return;
    clearTimeout(this.idleTimer);
    this.idleTimer = setTimeout(() => this.setState({ controlsActive: false }), CONTROLS_IDLE_MS);
    if (!this.state.controlsActive) this.setState({ controlsActive: true });
  }

  /* Dragging only moves the bar; the video seeks once, on release. `change`
     fires on release for a pointer and on every step for the keyboard, so the
     scrubber is operable without a mouse. `seeking` is raised by `input`
     alone, which a `change` always follows. */
  onSeekInput() {
    this.seeking = true;
    this.setState({ played: parseFloat(this.seek.value) || 0 });
    this.syncSeek();
  }

  onSeekChange() {
    const value = parseFloat(this.seek.value) || 0;
    const video = this.video;
    if (video && video.duration) video.currentTime = value * video.duration;
    this.seeking = false;
    this.setState({ played: value });
    /* The shopper's own step is spoken even while the clip plays. */
    this.syncSeek();
  }

  toggleFullscreen() {
    const video = this.video;
    if (video?.webkitEnterFullscreen && /iPad|iPhone|iPod/.test(navigator.userAgent)) {
      video.webkitEnterFullscreen();
      return;
    }
    if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {});
    else this.player?.requestFullscreen?.().catch(() => {});
  }

  onFullscreenChange() {
    const fullscreen = Boolean(this.player) && document.fullscreenElement === this.player;
    if (fullscreen !== this.state.fullscreen) this.setState({ fullscreen });
  }

  /* --- render ------------------------------------------------------------- */

  render() {
    const st = this.state;
    const mounted = st.playing || st.pausedByUser;

    if (mounted && !this.video && this.src) this.createVideo();
    if (!mounted && this.video) this.removeVideo();

    const video = this.video;
    if (video) {
      if (video.muted !== st.muted) video.muted = st.muted;
      /* Only on a change: a play() refused by the browser must not be retried
         on every progress tick. A refusal other than an interruption (an
         AbortError from a pause or a removed source) resets the player. */
      if (this.appliedPlaying !== st.playing) {
        this.appliedPlaying = st.playing;
        if (st.playing) {
          video.play()?.catch((error) => {
            if (error.name !== 'AbortError' && this.video === video) this.returnToCaption(() => this.reset());
          });
        } else {
          video.pause();
        }
      }
    }

    this.caption?.classList.toggle('is-shown', st.showCaption);
    this.player?.setAttribute('data-ready', String(st.ready));
    this.poster?.classList.toggle('is-hidden', st.ready && st.posterHidden);

    if (this.controller) {
      const active = st.ready && st.controlsActive && !st.showCaption;
      this.controller.classList.toggle('is-active', active);
      this.controller.classList.toggle('is-inactive', !active);
      this.controller.classList.toggle('is-playing', st.playing && st.played > 0);
      /* Any paused clip keeps its controls — at 0:00 too, or a keyboard user
         who tabs out of them could not get back in — once they can be seen:
         not under the poster of a clip paused before it could play, nor under
         the caption. canplay brings them up for such a clip. */
      this.controller.classList.toggle('is-paused', !st.playing && st.pausedByUser && st.ready && !st.showCaption);
    }

    if (this.toggleButton) {
      this.toggleButton.setAttribute('aria-label', this.label(st.playing ? 'pause' : 'play'));
      this.toggleButton.querySelector('[data-icon-play]').hidden = st.playing;
      this.toggleButton.querySelector('[data-icon-pause]').hidden = !st.playing;
    }

    if (this.muteButton) {
      this.muteButton.setAttribute('aria-label', this.label(st.muted ? 'unmute' : 'mute'));
      this.muteButton.querySelector('[data-icon-volume]').hidden = st.muted;
      this.muteButton.querySelector('[data-icon-muted]').hidden = !st.muted;
    }

    this.fullscreenButton?.setAttribute('aria-label', this.label(st.fullscreen ? 'exit-fullscreen' : 'fullscreen'));

    if (this.progress) {
      this.progress.style.setProperty('--played', st.played);
      this.progress.style.setProperty('--loaded', st.loaded);
    }
    /* A focused timeline is left alone while the clip plays: every change to
       its value is spoken, over the tutorial, about once a second. It catches
       up on a key press, on pause, and on the first tick after focus leaves. */
    if (!(st.playing && document.activeElement === this.seek)) this.syncSeek();
  }

  syncSeek() {
    if (!this.seek) return;
    const st = this.state;
    if (!this.seeking) this.seek.value = String(st.played);
    /* The value is a fraction of the clip; say it as a time instead — from the
       last known duration while a replay's new <video> loads its metadata. */
    const duration = this.video?.duration || this.duration;
    if (duration) {
      this.seek.setAttribute('aria-valuetext', this.label('timeline-value')
        .replace('[current]', formatTime(st.played * duration))
        .replace('[duration]', formatTime(duration)));
    } else {
      this.seek.removeAttribute('aria-valuetext');
    }
  }

  label(name) {
    return this.getAttribute(`data-label-${name}`) || '';
  }
}

if (!customElements.get('size-guide-video')) customElements.define('size-guide-video', SizeGuideVideo);
